import { parsePropertyCard } from "./parse";
import { EntityChip } from "../EntityChip";
import { PhaseBadge } from "./PhaseBadge";

const ENTITY_URL_PATTERN =
  /(?:map\.combify\.com|staging\.eu-west-1\.combify\.com|localhost(?::\d+)?)\/(property|project|permit|tender)\/([^\s)]+)/;

interface PropertyCardProps {
  content: string;
  onEntityClick?: (entityType: string, entityId: string, name: string) => void;
}

function EntityName({ link, label, onEntityClick }: {
  link: { name: string; url: string } | null;
  label: string;
  onEntityClick?: PropertyCardProps["onEntityClick"];
}) {
  if (link && onEntityClick) {
    const match = link.url.match(ENTITY_URL_PATTERN);
    if (match) {
      return (
        <EntityChip entityType={match[1]!} entityId={match[2]!} onClick={onEntityClick}>
          {label}
        </EntityChip>
      );
    }
  }
  return <span className="cx-property-name">{label}</span>;
}

export function PropertyCard({ content, onEntityClick }: PropertyCardProps) {
  const data = parsePropertyCard(content);
  if (!data) return null;

  const { header, metrics, ownership, plans, permits, zoning } = data;

  return (
    <div className="cx-property">
      {/* Header */}
      <div className="cx-property-header">
        <div>
          <div className="cx-property-name">
            <EntityName link={header.link} label={header.name} onEntityClick={onEntityClick} />
          </div>
          {(header.type || header.status) && (
            <div className="cx-property-type">
              {[header.type, header.status].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
        {header.badge && <span className="cx-property-badge">{header.badge}</span>}
      </div>

      {/* Metrics */}
      {metrics.length > 0 && (
        <div className="cx-property-metrics">
          {metrics.map((m, i) => (
            <div key={i} className="cx-property-metric">
              <div className={`cx-property-metric-value${i === 0 ? " cx-property-metric-value--accent" : ""}`}>
                {m.value}
              </div>
              <div className="cx-property-metric-label">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Ownership */}
      {ownership.length > 0 && (
        <div className="cx-property-section">
          <div className="cx-property-section-title">Ownership</div>
          {ownership.map((o, i) => (
            <div key={i} className="cx-property-owner-row">
              <span className="cx-property-owner-name">{o.name}</span>
              <span className="cx-property-owner-share">
                {o.share}%
                <span className="cx-property-owner-bar">
                  <span className="cx-property-owner-bar-fill" style={{ width: `${o.share}%` }} />
                </span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <div className="cx-property-section">
          <div className="cx-property-section-title">Connected plans</div>
          {plans.map((p, i) => (
            <div key={i} className="cx-property-plan-row">
              <EntityName link={p.link} label={p.label} onEntityClick={onEntityClick} />
              {p.phase && <PhaseBadge value={p.phase} />}
              {p.meta && <span className="cx-property-plan-meta">{p.meta}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Permits */}
      {permits.length > 0 && (
        <div className="cx-property-section">
          <div className="cx-property-section-title">Permits</div>
          {permits.map((p, i) => (
            <div key={i} className="cx-property-plan-row">
              <EntityName link={p.link} label={p.label} onEntityClick={onEntityClick} />
              {p.status && <PhaseBadge value={p.status} />}
              {p.meta && <span className="cx-property-plan-meta">{p.meta}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Zoning */}
      {zoning.length > 0 && (
        <div className="cx-property-section">
          <div className="cx-property-section-title">Zoning</div>
          <div className="cx-property-zoning-tags">
            {zoning.map((z, i) => (
              <span key={i} className="cx-property-zoning-tag">{z}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
